import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { supabase } from '@/lib/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useGestor } from '@/contexts/GestorContext'
import { toast } from 'sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { VW_ORCAMENTO_V2, num, type OrcamentoV2Linha } from '@/lib/gestor-orcamento-v2'

type Nivel = 'n1' | 'n2' | 'n3'

type Agregado = {
  chave: string
  rotulo: string
  orcado: number
  comprometido: number
  pago: number
  pct: number
}

function agregar(rows: OrcamentoV2Linha[], nivel: Nivel): Agregado[] {
  const map = new Map<string, Agregado>()

  for (const r of rows) {
    const n1 = (r.tipo_receita_despesa || '').trim() || '(sem N1)'
    const n2 = (r.classificacao_receita_despesa || '').trim() || '(sem N2)'
    const n3 = (r.subtipo_despesa || '').trim() || '(sem N3)'

    let chave: string
    let rotulo: string
    if (nivel === 'n1') {
      chave = n1
      rotulo = n1
    } else if (nivel === 'n2') {
      chave = `${n1} › ${n2}`
      rotulo = chave
    } else {
      chave = r.chave_unica_v2 || `${n1}|${n2}|${n3}`
      rotulo = `${n1} › ${n2} › ${n3}`
    }

    const o = num(r.valor_total_orcado)
    const c = num(r.valor_comprometido)
    const p = num(r.valor_pago)
    const cur = map.get(chave) || { chave, rotulo, orcado: 0, comprometido: 0, pago: 0, pct: 0 }
    cur.orcado += o
    cur.comprometido += c
    cur.pago += p
    map.set(chave, cur)
  }

  const list = Array.from(map.values()).map((x) => ({
    ...x,
    pct: x.orcado > 0 ? (x.comprometido / x.orcado) * 100 : 0,
  }))
  list.sort((a, b) => b.orcado - a.orcado)
  return list
}

function toChartData(agregados: Agregado[]) {
  return agregados.slice(0, 12).map((a) => ({
    name: a.rotulo.length > 28 ? `${a.rotulo.slice(0, 26)}…` : a.rotulo,
    Orçado: Math.round(a.orcado * 100) / 100,
    Comprometido: Math.round(a.comprometido * 100) / 100,
    Pago: Math.round(a.pago * 100) / 100,
  }))
}

function PainelNivel({
  nivel,
  label,
  agregados,
  area,
  ano,
}: {
  nivel: 'n1' | 'n2' | 'n3'
  label: string
  agregados: Agregado[]
  area: string
  ano: string
}) {
  const chartData = useMemo(() => toChartData(agregados), [agregados])
  const formatCurrency = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)

  return (
    <TabsContent value={nivel} className="space-y-4 mt-4">
      <Card className="border-none shadow-sm bg-white">
        <CardHeader>
          <CardTitle className="text-base">Gráfico — top {Math.min(12, agregados.length)} ({label})</CardTitle>
          <CardDescription>
            Área {area} · {ano}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[400px] w-full">
            {chartData.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-16">Sem dados para o período.</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 64 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis
                    dataKey="name"
                    angle={-35}
                    textAnchor="end"
                    height={90}
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    interval={0}
                  />
                  <YAxis
                    tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                  />
                  <Tooltip
                    formatter={(value: number) => formatCurrency(value)}
                    contentStyle={{ borderRadius: 8, border: 'none' }}
                  />
                  <Legend />
                  <Bar dataKey="Orçado" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Comprometido" fill="#f97316" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Pago" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-none shadow-sm bg-white">
        <CardHeader>
          <CardTitle className="text-base">Tabela detalhada</CardTitle>
          <CardDescription>% = comprometido ÷ orçado (quando orçado &gt; 0).</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-slate-50/95 backdrop-blur">
                <TableRow>
                  <TableHead className="pl-4">Nível</TableHead>
                  <TableHead className="text-right">Orçado</TableHead>
                  <TableHead className="text-right">Comprometido</TableHead>
                  <TableHead className="text-right">Pago</TableHead>
                  <TableHead className="text-right">% uso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agregados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-12">
                      Nenhum dado.
                    </TableCell>
                  </TableRow>
                ) : (
                  agregados.map((a) => (
                    <TableRow key={a.chave} className="border-slate-50">
                      <TableCell className="pl-4 max-w-md">
                        <span className="text-sm text-slate-800 line-clamp-2" title={a.rotulo}>
                          {a.rotulo}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-sm">{formatCurrency(a.orcado)}</TableCell>
                      <TableCell className="text-right text-sm">{formatCurrency(a.comprometido)}</TableCell>
                      <TableCell className="text-right text-sm">{formatCurrency(a.pago)}</TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {a.orcado > 0 ? `${a.pct.toFixed(1)}%` : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  )
}

export default function GestorRealizadoOrcadoPage() {
  const { areaSelecionada, anoSelecionado } = useGestor()
  const [rows, setRows] = useState<OrcamentoV2Linha[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    if (!anoSelecionado || !areaSelecionada) return
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from(VW_ORCAMENTO_V2)
        .select('*')
        .eq('ano', parseInt(anoSelecionado, 10))
        .eq('centro_resultado', areaSelecionada)

      if (error) throw error
      setRows((data as OrcamentoV2Linha[]) || [])
    } catch (e) {
      console.error(e)
      toast.error('Erro ao carregar dados para comparativo.')
    } finally {
      setLoading(false)
    }
  }, [anoSelecionado, areaSelecionada])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const ag1 = useMemo(() => agregar(rows, 'n1'), [rows])
  const ag2 = useMemo(() => agregar(rows, 'n2'), [rows])
  const ag3 = useMemo(() => agregar(rows, 'n3'), [rows])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#131E29]/20 border-t-orange-500 border-r-[#131E29]" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <Tabs defaultValue="n1" className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="n1">Por macro</TabsTrigger>
          <TabsTrigger value="n2">Macro + classificação</TabsTrigger>
          <TabsTrigger value="n3">Por linha (subtipo)</TabsTrigger>
        </TabsList>

        <PainelNivel
          nivel="n1"
          label="macro"
          agregados={ag1}
          area={areaSelecionada}
          ano={anoSelecionado}
        />
        <PainelNivel
          nivel="n2"
          label="macro + classificação"
          agregados={ag2}
          area={areaSelecionada}
          ano={anoSelecionado}
        />
        <PainelNivel
          nivel="n3"
          label="linha (subtipo)"
          agregados={ag3}
          area={areaSelecionada}
          ano={anoSelecionado}
        />
      </Tabs>
    </div>
  )
}
